import { useForm } from '@tanstack/react-form';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { DATE_FORMATS } from '../../utils/date';
import { DateTimePicker } from '../atoms/DateTimePicker';
import { Button } from '../ui/button';
import { Field, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Spinner } from '../ui/spinner';
import { Textarea } from '../ui/textarea';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

export type { ValueSetCode };

/**
 * Subset of LOINC's `SCALE_TYP` we render input shapes for.
 * - `Qn` — quantitative (number + unit)
 * - `Ord` — ordinal (e.g. Reactive / Non-reactive)
 * - `Nar` — narrative (free-text)
 *
 * `Nom` (nominal) is treated like `Ord` — same input shape applies.
 */
export type LabScaleType = 'Qn' | 'Ord' | 'Nar';

const formSchema = z.object({
  labType: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  value: z.string().trim().min(1),
  effectiveDateTime: z.string().min(1),
  note: z.string().trim(),
});

type RawFormValues = z.infer<typeof formSchema>;

export type AddLabResultFormValues = RawFormValues & {
  scaleType: LabScaleType;
  unit: string;
};

/**
 * Add Lab Result form.
 *
 * @remarks
 * The form is fully controlled by props for `scaleType`, `unit`, and
 * `ordinalChoices`. Consumers are responsible for:
 *
 *   - Keeping `scaleType` and `unit` in sync with the *current* selected lab.
 *     The form reacts to prop changes but does not validate that the props
 *     match the picked code.
 *   - Avoiding rendering the form in a partially-resolved state if it matters
 *     for UX. `canSubmit` is set to `false` when required props are absent,
 *     but it is updated only on form-field mutations — not on prop changes.
 *
 * When patient-web adopts this form as a second consumer, extract a
 * controller hook (e.g. `useAddLabResultFormController`) that encodes these
 * responsibilities so neither consumer can violate the contract by accident.
 * See `useHealthGorillaLabOrder` in `@medplum/health-gorilla-react` for prior
 * art on this shape.
 */
export interface AddLabResultFormProps {
  onCreate: (values: AddLabResultFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  /** ValueSet expansion driving the lab-type combobox. */
  labType: UseValueSetExpandResult;
  /** Fires when the user picks (or clears) a lab type. Caller can use this to look up scale + unit. */
  onLabTypeChange?: (value: ValueSetCode | undefined) => void;
  /**
   * LOINC scale of the selected code. Defaults to `Qn` so the form shows a numeric+unit input
   * before the caller resolves the actual scale.
   */
  scaleType?: LabScaleType;
  /** Whether the scale lookup is in flight. */
  isScaleLoading?: boolean;
  /**
   * Unit for the selected lab type, sourced from LOINC's `EXAMPLE_UCUM_UNITS`. Renders as a
   * read-only display next to the value input. When undefined, the input is empty and the form
   * cannot submit — every code in the curated ValueSet has a known unit, so this case shouldn't
   * fire in practice; if it does, it's a bug at the lookup layer rather than a UX state.
   */
  unit?: string;
  /** Whether the unit lookup is in flight. */
  isUnitLoading?: boolean;
  /**
   * Allowed answers for `scaleType === 'Ord'`. When non-empty, the value field renders as a
   * dropdown over these choices. When omitted or empty, the value is free-text.
   */
  ordinalChoices?: string[];
  /**
   * Pre-selected lab type. Used by stories (and future edit flows) so the value section can be
   * exercised without driving the combobox interactively. The form leaves the field empty by
   * default.
   */
  defaultLabType?: ValueSetCode;
}

const nowLocalDateTime = (): string => format(new Date(), DATE_FORMATS.ISO_DATETIME_LOCAL);

type FormFieldValues = z.input<typeof formSchema>;

const validateValues = (
  value: FormFieldValues,
  scaleType: LabScaleType,
  resolvedUnit: string,
): 'invalid' | undefined => {
  const base = formSchema.safeParse(value);
  if (!base.success) return 'invalid';
  if (scaleType === 'Qn' && resolvedUnit.length === 0) return 'invalid';
  if (scaleType === 'Qn' && Number.isNaN(Number(value.value))) return 'invalid';
  return undefined;
};

export function AddLabResultForm({
  onCreate,
  onClose,
  isCreating,
  labType,
  onLabTypeChange,
  scaleType = 'Qn',
  isScaleLoading = false,
  unit,
  isUnitLoading = false,
  ordinalChoices,
  defaultLabType,
}: AddLabResultFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const resolvedUnit = unit ?? '';

  const formApi = useForm({
    defaultValues: {
      labType: defaultLabType,
      value: '',
      effectiveDateTime: nowLocalDateTime(),
      note: '',
    },
    // Form-internal schema check + Qn-specific cross-cutting guards. `unit` and `scaleType`
    // come from props, not form state, so they're checked here rather than in the schema.
    // Wired to both `onChange` (to keep `canSubmit` accurate) and `onSubmit` (defense in depth
    // if `canSubmit` ever drifts from actual validity, e.g. mid-flight prop changes).
    validators: {
      onMount: formSchema,
      onChange: ({ value }) => validateValues(value, scaleType, resolvedUnit),
      onSubmit: ({ value }) => validateValues(value, scaleType, resolvedUnit),
    },
    onSubmit: async ({ value }) => {
      await onCreate({ ...formSchema.parse(value), scaleType, unit: resolvedUnit });
    },
  });

  const isLoading = isScaleLoading || isUnitLoading;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void formApi.handleSubmit();
      }}
      className="space-y-3"
    >
      <formApi.Field name="labType">
        {(field) => (
          <ValueSetCombobox
            id={field.name}
            label={t('labType')}
            placeholder={t('labTypePlaceholder')}
            items={labType.items}
            value={field.state.value ?? null}
            onValueChange={(value) => {
              field.handleChange(value ?? undefined);
              // Clear any value typed for the previous lab. The new lab's scale may render a
              // different input shape (e.g. Qn → Ord), and a leftover string would be invalid for
              // the new shape. Also fires the `onChange` validator so `canSubmit` updates promptly.
              formApi.setFieldValue('value', '');
              onLabTypeChange?.(value ?? undefined);
            }}
            onQueryChange={labType.onQueryChange}
            isLoading={labType.isFetching}
            error={labType.error}
            emptyMessage={t('noMatchingResults')}
            required
          />
        )}
      </formApi.Field>

      <formApi.Subscribe selector={(state) => state.values.labType}>
        {(selectedLabType) =>
          selectedLabType === undefined ? null : isScaleLoading ? (
            <Field>
              <FieldLabel>{t('value')}</FieldLabel>
              <div className="flex h-9 items-center justify-center rounded-md border border-input bg-muted/50">
                <Spinner className="size-4" />
              </div>
            </Field>
          ) : scaleType === 'Qn' ? (
            <div className="flex gap-3">
              <formApi.Field name="value">
                {(field) => (
                  <Field className="flex-2">
                    <FieldLabel htmlFor={field.name}>
                      {t('value')}
                      <span aria-hidden="true">*</span>
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="number"
                      inputMode="decimal"
                      step="any"
                      required
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('valuePlaceholder')}
                      aria-label={t('value')}
                    />
                  </Field>
                )}
              </formApi.Field>

              <Field className="flex-1">
                <FieldLabel htmlFor="lab-result-unit">
                  {t('unit')}
                  <span aria-hidden="true">*</span>
                </FieldLabel>
                {isUnitLoading ? (
                  <div className="flex h-9 items-center justify-center rounded-md border border-input bg-muted/50">
                    <Spinner className="size-4" />
                  </div>
                ) : (
                  <Input
                    id="lab-result-unit"
                    name="unit"
                    required
                    readOnly
                    value={resolvedUnit}
                    placeholder={t('unitPlaceholder')}
                    aria-label={t('unit')}
                  />
                )}
              </Field>
            </div>
          ) : scaleType === 'Ord' ? (
            <formApi.Field name="value">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>
                    {t('value')}
                    <span aria-hidden="true">*</span>
                  </FieldLabel>
                  {ordinalChoices && ordinalChoices.length > 0 ? (
                    <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                      <SelectTrigger id={field.name} aria-label={t('value')}>
                        <SelectValue placeholder={t('ordinalValuePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {ordinalChoices.map((choice) => (
                          <SelectItem key={choice} value={choice}>
                            {choice}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={field.name}
                      name={field.name}
                      required
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('ordinalValuePlaceholder')}
                      aria-label={t('value')}
                    />
                  )}
                </Field>
              )}
            </formApi.Field>
          ) : (
            <formApi.Field name="value">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>
                    {t('value')}
                    <span aria-hidden="true">*</span>
                  </FieldLabel>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    required
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('narrativeValuePlaceholder')}
                    aria-label={t('value')}
                    rows={3}
                  />
                </Field>
              )}
            </formApi.Field>
          )
        }
      </formApi.Subscribe>

      <formApi.Field name="effectiveDateTime">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>
              {t('effectiveDateTime')}
              <span aria-hidden="true">*</span>
            </FieldLabel>
            <DateTimePicker
              id={field.name}
              required
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(value) => field.handleChange(value)}
              aria-label={t('effectiveDateTime')}
            />
          </Field>
        )}
      </formApi.Field>

      <formApi.Field name="note">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>{t('note')}</FieldLabel>
            <Input
              id={field.name}
              name={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder={t('notePlaceholder')}
              aria-label={t('note')}
            />
          </Field>
        )}
      </formApi.Field>

      <div className="flex gap-2">
        <formApi.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating || isLoading}
              className="flex-1"
              aria-label={t('saveNewLabResult')}
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </formApi.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddLabResult')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
